import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Person,
  AccessTime,
  Phone,
  Email,
  Edit,
  Cancel,
  CheckCircle,
  PlayArrow,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Appointment } from '../../types/appointment';

interface AppointmentDetailProps {
  open: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  onEdit?: () => void;
  onCancel?: () => void;
  onConfirm?: () => void;
  onStart?: () => void;
  onComplete?: () => void;
}

const AppointmentDetail: React.FC<AppointmentDetailProps> = ({
  open,
  onClose,
  appointment,
  onEdit,
  onCancel,
  onConfirm,
  onStart,
  onComplete,
}) => {
  if (!appointment) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'confirmed':
        return 'Confirmed';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Scheduled';
    }
  };

  const getAvailableActions = () => {
    const actions = [];
    
    switch (appointment.status) {
      case 'scheduled':
        if (onConfirm) actions.push({ label: 'Confirm', action: onConfirm, icon: <CheckCircle />, color: 'success' });
        if (onCancel) actions.push({ label: 'Cancel', action: onCancel, icon: <Cancel />, color: 'error' });
        break;
      case 'confirmed':
        if (onStart) actions.push({ label: 'Start', action: onStart, icon: <PlayArrow />, color: 'warning' });
        if (onCancel) actions.push({ label: 'Cancel', action: onCancel, icon: <Cancel />, color: 'error' });
        break;
      case 'in_progress':
        if (onComplete) actions.push({ label: 'Complete', action: onComplete, icon: <CheckCircle />, color: 'info' });
        break;
    }
    
    return actions;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6">
                Appointment Details
              </Typography>
              <Chip
                label={getStatusLabel(appointment.status)}
                color={getStatusColor(appointment.status) as any}
                size="small"
              />
            </Box>
          </Box>
          {onEdit && appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
            <Tooltip title="Edit Appointment">
              <IconButton onClick={onEdit}>
                <Edit />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Customer Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <Person sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText 
                      primary={appointment.customerName} 
                      secondary="Customer Name" 
                    />
                  </ListItem>
                  {appointment.customerPhone && (
                    <ListItem sx={{ px: 0 }}>
                      <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText 
                        primary={appointment.customerPhone} 
                        secondary="Phone Number" 
                      />
                    </ListItem>
                  )}
                  {appointment.customerEmail && (
                    <ListItem sx={{ px: 0 }}>
                      <Email sx={{ mr: 2, color: 'text.secondary' }} />
                      <ListItemText 
                        primary={appointment.customerEmail} 
                        secondary="Email Address" 
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Appointment Information
                </Typography>
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <AccessTime sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText 
                      primary={format(new Date(appointment.appointmentDate), 'EEEE, MMM dd, yyyy')} 
                      secondary="Date" 
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <AccessTime sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText 
                      primary={`${appointment.startTime} - ${appointment.endTime}`} 
                      secondary="Time" 
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <Person sx={{ mr: 2, color: 'text.secondary' }} />
                    <ListItemText 
                      primary={appointment.staffName} 
                      secondary="Staff Member" 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Services */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Services
                </Typography>
                <List>
                  {appointment.services.map((service, index) => (
                    <React.Fragment key={service.serviceId}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary={service.serviceName}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Duration: {service.duration} minutes
                              </Typography>
                              <Typography variant="body2" color="success.main">
                                ₹{service.price}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < appointment.services.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    Total Amount
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    ₹{appointment.totalAmount}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Notes */}
          {appointment.notes && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {appointment.notes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Appointment Actions */}
          {getAvailableActions().length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Actions
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {getAvailableActions().map((action, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        color={action.color as any}
                        startIcon={action.icon}
                        onClick={action.action}
                      >
                        {action.label}
                      </Button>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentDetail;